import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';

import {BaseService} from "@6grain/masofa-monolith-api-client/dictionaries/api.base.service";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {mergeParams, reverseFilterQuery} from '@app/utils/_msf';
import {SortType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/sortType';
import {FilterOperator} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/filterOperator';

export interface FieldFilter {
  filterField?: string | null;
  filterValue?: any | null;
  filterOperator?: FilterOperator;
}

export interface RequestParams {
  take?: number | null;
  offset?: number;
  sortBy?: string | null;
  sort?: SortType;
  filters?: Array<FieldFilter> | null;
  [key: string]: any;
}

@Component({
  template: '',
  standalone: true,
})
export class ResourceComponent implements OnInit, OnDestroy {
  public $destroy$: Subject<void> = new Subject<void>();
  public cdr!: ChangeDetectorRef;
  public loading = false;
  public firstTime = true;
  public first: number = 0;
  public params: RequestParams = {};
  public totalRecords: number = 0


  @Input() storageKey: string = '';
  @Input() totalRequestNoSendFilter: string [] = []

  @Input() data: any;
  @Input() service?: BaseService | any;
  @Input() getAllMethod?: BaseService | any;
  @Input() getTotalMethod?: string | any;
  @Input() filter: Array<any> = [];
  @Input() forceRequest = false;
  @Input() methodWithParams = true;


  @Input() defaultParams: RequestParams = {
    take: 10,
    offset: 0,
    sort: SortType.NUMBER_1,
    filters: []
  }

  private static readonly RESERVED_PARAM_KEYS = new Set([
    'take',
    'offset',
    'sort',
    'sortBy',
    'filters',
  ]);

  @Input() set toggleFilter(value: boolean | undefined) {
    if (!this.firstTime) {
      this.first = 0;
      this.params.offset = 0;
      this.syncExtraParamsFromDefault();
      mergeParams(this.params, {
        take: this.defaultParams.take,
        sort: this.defaultParams.sort,
        sortBy: this.defaultParams.sortBy,
      });
      this.loadData();
    }
  }

  @Output() OnFilterSaved = new EventEmitter();

  @Output() loadingChange = new EventEmitter();

  @Output() currentPageData = new EventEmitter<number>();

  ngOnInit() {
    this.initParams();
    this.loadData();
    this.firstTime = false;
  }

  ngOnDestroy() {
    this.$destroy$.next();
    this.$destroy$.complete();
  }

  /** Sync custom query keys (e.g. seasonScope) from defaultParams into params. */
  private syncExtraParamsFromDefault(): void {
    for (const key of Object.keys(this.params)) {
      if (
        !ResourceComponent.RESERVED_PARAM_KEYS.has(key) &&
        !(key in this.defaultParams)
      ) {
        delete this.params[key];
      }
    }

    for (const [key, value] of Object.entries(this.defaultParams)) {
      if (ResourceComponent.RESERVED_PARAM_KEYS.has(key)) {
        continue;
      }
      if (value !== undefined && value !== null) {
        this.params[key] = value;
      } else {
        delete this.params[key];
      }
    }
  }

  private initParams() {
    try {

      const savedParams = localStorage.getItem(this.storageKey);

      if (savedParams && this.storageKey) {
        const parsedParams: RequestParams = JSON.parse(savedParams);

        if (parsedParams && parsedParams.filters) {
          this.filter = parsedParams.filters;
        }

        mergeParams(this.params, this.defaultParams);
        mergeParams(this.params, parsedParams);

        this.params.offset = this.defaultParams.offset;
        this.params.take = this.defaultParams.take;


      } else {
        mergeParams(this.params, this.defaultParams);
      }
    } catch (error) {
      console.error('Error loading params from localStorage:', error);
    }
  }

  private saveParamsToStorage(params: RequestParams) {
    if (!this.storageKey) {
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(params));
    } catch (error) {
      console.error('Error saving params to localStorage:', error);
    }
  }

  loadData() {
    if (this.service && this.getAllMethod) {
      this.loading = true;

      let params: RequestParams = {
        ...this.params,
      };

      if (this.filter) {
        params = {
          ...params,
          filters: this.filter as any
        };
      }


      let serviceMethod
      if (this.methodWithParams) {
        serviceMethod = this.service[this.getAllMethod](params);
      } else {
        serviceMethod = this.service[this.getAllMethod]();
      }

      this.loadTotal(params);

      serviceMethod
        .pipe(takeUntil(this.$destroy$))
        .subscribe((data: any) => {

          this.loading = false;

          this.data = Array.isArray(data) ? data : [];
          this.currentPageData.emit(this.data);

          this.saveParamsToStorage(params);

          this.loadingChange.emit(this.loading);

          this.OnFilterSaved.emit(reverseFilterQuery(this.filter as []));

          if (this.cdr) {
            this.cdr.markForCheck();
          }
        }, () => {
          this.loading = false;
          this.data = [];
          this.loadingChange.emit(this.loading);
          if (this.cdr) {
            this.cdr.markForCheck();
          }
        });
    }
  }

  loadTotal(params = {}) {
    if (this.service && this.getTotalMethod) {
      const finalParams: RequestParams = {...params};
      delete finalParams.take;
      delete finalParams.offset;
      delete finalParams.sortBy;

      if (this.totalRequestNoSendFilter.length && finalParams.filters) {
        finalParams.filters = (finalParams.filters as []).filter((filter: FieldFilter) => !this.totalRequestNoSendFilter.includes(filter.filterField as string))
      }

      this.service[this.getTotalMethod](finalParams)
        .pipe(takeUntil(this.$destroy$))
        .subscribe(
          (totalRecords: number) => {
            this.totalRecords = typeof totalRecords === 'number' ? totalRecords : 0;
            if (this.cdr) {
              this.cdr.markForCheck();
            }
          },
          () => {
            this.totalRecords = 0;
            if (this.cdr) {
              this.cdr.markForCheck();
            }
          }
        )
    }
  }
}
